<script setup>
import { computed , onMounted } from 'vue'
import { installTelegramDeepLinks } from '@/lib/telegramLinks'
import { usePage, router } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import BrandHeader from '@/Components/Public/BrandHeader.vue'
import PublicNav from '@/Components/Public/PublicNav.vue'
import SiteFooter from '@/Components/Public/SiteFooter.vue'
import HeaderMenu from '@/Components/Public/HeaderMenu.vue'
import PlatformIcon from '@/Components/Public/PlatformIcon.vue'
import ThemeMenu from '@/Components/Public/ThemeMenu.vue'

// Telegram links open the app directly on desktop (t.me is DNS-hijacked on some Thai ISPs).
onMounted(installTelegramDeepLinks)

const page = usePage()
const flash = computed(() => page.props.flash || {})
const currentLocale = computed(() => page.props.locale || 'en')
const mainSite = computed(() => page.props.main_site || 'https://getphuket.com')
const brand = computed(() => page.props.brand || {})
const sf = computed(() => page.props.site_footer || {})
const localePrefix = computed(() => (!currentLocale.value || currentLocale.value === 'en') ? '' : `/${currentLocale.value}`)
const { t } = useI18n()

function setLang(lang) {
  if (lang === currentLocale.value) return
  // Preserve ?sent=1 across a post-submit language switch — otherwise the
  // thank-you state (showForm computed off `sent`) silently reverts to the
  // form on the very next request.
  const keepSent = new URLSearchParams(window.location.search).has('sent')
  const params = { lang, ...(keepSent ? { sent: 1 } : {}) }
  router.get(window.location.pathname, params, { preserveScroll: true, preserveState: false })
}

const localeOptions = [
  { value: 'en', label: 'English', sub: 'EN' },
  { value: 'ru', label: 'Русский', sub: 'RU' },
  { value: 'zh', label: '中文',    sub: 'ZH' },
  { value: 'th', label: 'ไทย',     sub: 'TH' }
]
const currentLocaleLabel = computed(() => (localeOptions.find((o) => o.value === currentLocale.value) || localeOptions[0]).label)

</script>

<template>
  <div class="mkt public-shell">
    <BrandHeader :home-href="mainSite">
      <template #nav>
        <PublicNav external />
      </template>
      <template #tools>
        <div class="public-header__tools">
          <HeaderMenu :aria-label="t('public.menu.language')" wide>
            <template #icon>
              <PlatformIcon platform="globe" size="1.05rem" />
              <span class="hmenu__label">{{ currentLocaleLabel }}</span>
            </template>
            <template #default="{ close }">
              <button v-for="opt in localeOptions" :key="opt.value" type="button" class="hmenu__item" @click="setLang(opt.value); close()">
                <span>{{ opt.label }} <span class="hmenu__item-sub">({{ opt.sub }})</span></span>
                <svg v-if="opt.value === currentLocale" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="hmenu__check" aria-hidden="true">
                  <polyline points="5 12 10 17 19 7"/>
                </svg>
              </button>
            </template>
          </HeaderMenu>
          <ThemeMenu />
          <PublicNav variant="menu" external />
        </div>
      </template>
    </BrandHeader>

    <div
      v-if="flash.alert"
      class="mkt__flash mkt__flash--alert"
      role="alert"
    >{{ flash.alert }}</div>
    <div
      v-if="flash.notice"
      class="mkt__flash mkt__flash--notice"
    >{{ flash.notice }}</div>

    <main class="mkt__body"><slot /></main>

    <SiteFooter :hosts="sf.hosts" :channels="sf.channels" :invest-url="sf.invest_url" :painting-url="sf.painting_url" :partners-base="sf.partners_base" :locale-prefix="localePrefix" :brand-name="brand.name || 'Get Phuket'" :locale="currentLocale" :locale-options="localeOptions" @select-lang="setLang" />
  </div>
</template>

<style scoped>
.mkt { min-height: 100vh; display: flex; flex-direction: column; background: var(--bg); }
.mkt__flash { max-width: 64rem; margin: 0.75rem auto 0; width: calc(100% - 2rem); display: flex; align-items: center; gap: 0.5rem; padding: 0.75rem 1rem; border-radius: var(--radius-input, 0.5rem); font-size: 0.9375rem; }
.mkt__flash--alert { background: color-mix(in oklch, var(--danger) 12%, var(--surface)); color: var(--danger); }
.mkt__flash--notice { background: color-mix(in oklch, var(--ok) 12%, var(--surface)); color: var(--ok); }
.mkt__body { flex: 1; }
</style>
